import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  Calendar, 
  Lock, 
  User, 
  X, 
  Check, 
  Loader, 
  AlertCircle,
  Shield,
  Tag,
  DollarSign,
  RefreshCw,
  Gift,
  Wallet,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';

// --- Configuración de Axios ---
const apiClient = axios.create({
  baseURL: 'https://backend-production-1786.up.railway.app/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir token
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error("Error setting up Axios request:", error);
    return Promise.reject(error);
  }
);

const fadeIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2 }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: 20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.3, ease: "easeOut" }
  }
};

const MercadoPagoModal = ({ 
  isOpen, 
  onClose, 
  planInfo, 
  billingCycle, 
  onPaymentSuccess,
  onPaymentError,
  precioOriginal = 0
}) => {
  const [paymentStep, setPaymentStep] = useState('card-details');
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  
  // Estados para CVV de tarjeta guardada
  const [savedCardCvv, setSavedCardCvv] = useState('');
  const [savedCardCvvError, setSavedCardCvvError] = useState('');
  
  // Estados para Cupones
  const [codigo, setCodigo] = useState('');
  const [loadingCupon, setLoadingCupon] = useState(false);
  const [errorCupon, setErrorCupon] = useState(null);
  const [cuponAplicado, setCuponAplicado] = useState(null);
  
  // Estados para Saldo
  const [saldo, setSaldo] = useState(0);
  const [loadingSaldo, setLoadingSaldo] = useState(true);
  const [errorSaldo, setErrorSaldo] = useState(null);
  const [usarSaldo, setUsarSaldo] = useState(false);
  const [saldoAplicado, setSaldoAplicado] = useState(0);
  
  // Estado para el precio final
  const [precioFinal, setPrecioFinal] = useState(precioOriginal);
  
  // Estados específicos de Mercado Pago
  const [mpPreferenceId, setMpPreferenceId] = useState(null);
  const [mpInitPoint, setMpInitPoint] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('checkout'); // 'checkout' o 'card'

  // Detectar móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Resetear estados cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setPaymentStep('card-details');
      setProcessing(false);
      setError(null);
      setCuponAplicado(null);
      setCodigo('');
      setErrorCupon(null);
      setUsarSaldo(false);
      setSaldoAplicado(0);
      setPrecioFinal(precioOriginal);
      setFormErrors({});
      setSelectedCard(null);
      setSavedCardCvv('');
      setSavedCardCvvError('');
      setMpPreferenceId(null);
      setMpInitPoint(null);
      setPaymentMethod('checkout');
      setPaymentForm({
        cardNumber: '',
        cardHolder: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: ''
      });
    }
  }, [isOpen, precioOriginal]);

  // Cargar saldo al abrir el modal
  useEffect(() => {
    if (isOpen) {
      const fetchSaldo = async () => {
        try {
          setLoadingSaldo(true);
          setErrorSaldo(null);
          
          const response = await apiClient.get('/cupones/saldo');
          setSaldo(response.data.saldo || 0);
        } catch (error) {
          console.error('Error al obtener saldo:', error);
          setErrorSaldo('No se pudo cargar tu saldo.');
          setSaldo(0);
        } finally {
          setLoadingSaldo(false);
        }
      };
      
      fetchSaldo();
    }
  }, [isOpen]);

  // Cargar métodos de pago guardados (Mercado Pago)
  useEffect(() => {
    if (isOpen) {
      const fetchSavedCards = async () => {
        try {
          const response = await apiClient.get('/pagos/methods');
          setSavedCards(response.data.paymentMethods || []);
        } catch (err) {
          console.error('Error fetching saved payment methods MP:', err);
          setSavedCards([]);
        }
      };
      
      fetchSavedCards();
    }
  }, [isOpen]);

  // Actualizar precio final cuando cambie algún descuento
  useEffect(() => {
    let nuevoTotal = parseFloat(precioOriginal) || 0;
    
    // Aplicar descuento de cupón si existe
    if (cuponAplicado && cuponAplicado.descuento) {
      if (cuponAplicado.cupon.tipo === 'meses_gratis') {
        if (billingCycle === 'mensual') {
          nuevoTotal = 0;
        } else {
          const descuentoMensual = precioOriginal / 12 * cuponAplicado.cupon.valor;
          nuevoTotal = Math.max(0, precioOriginal - descuentoMensual);
        }
      } else {
        nuevoTotal = cuponAplicado.descuento.montoFinal || 0;
      }
    }
    
    // Aplicar saldo si se ha seleccionado
    if (saldoAplicado > 0) {
      nuevoTotal = Math.max(0, nuevoTotal - saldoAplicado);
    }
    
    setPrecioFinal(nuevoTotal);
    
  }, [cuponAplicado, saldoAplicado, precioOriginal, billingCycle]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    let error = null;
    
    if (name === 'cardNumber') {
      processedValue = value.replace(/\D/g, '')
                            .replace(/(\d{4})(?=\d)/g, '$1 ')
                            .trim();
                            
      if (processedValue && processedValue.replace(/\s/g, '').length < 16) {
        error = 'El número de tarjeta debe tener 16 dígitos';
      }
    }
    
    if (name === 'cardHolder') {
      if (value && value.length < 3) {
        error = 'Ingrese el nombre del titular';
      }
    }
    
    if (name === 'expiryMonth') {
      processedValue = value.replace(/\D/g, '').slice(0, 2);
      const month = parseInt(processedValue);
      if (processedValue && (month < 1 || month > 12)) {
        error = 'Mes inválido';
      }
    }
    
    if (name === 'expiryYear') {
      processedValue = value.replace(/\D/g, '').slice(0, 2);
      const year = parseInt(processedValue);
      const currentYear = new Date().getFullYear() % 100;
      if (processedValue && year < currentYear) {
        error = 'Año inválido';
      }
    }
    
    if (name === 'cvv') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
      if (processedValue && processedValue.length < 3) {
        error = 'CVV debe tener al menos 3 dígitos';
      }
    }
    
    setPaymentForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    if (!paymentForm.cardNumber || paymentForm.cardNumber.replace(/\s/g, '').length !== 16) {
      errors.cardNumber = 'Número de tarjeta inválido';
      isValid = false;
    }
    
    if (!paymentForm.cardHolder || paymentForm.cardHolder.length < 3) {
      errors.cardHolder = 'Nombre del titular requerido';
      isValid = false;
    }
    
    const month = parseInt(paymentForm.expiryMonth);
    const year = parseInt(paymentForm.expiryYear);
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    
    if (!month || month < 1 || month > 12) {
      errors.expiryMonth = 'Mes inválido';
      isValid = false;
    }
    
    if (!year || year < currentYear || (year === currentYear && month < currentMonth)) {
      errors.expiryYear = 'Fecha expirada';
      isValid = false;
    }
    
    if (!paymentForm.cvv || paymentForm.cvv.length < 3) {
      errors.cvv = 'CVV inválido';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };

  const selectSavedCard = (card) => {
    setSelectedCard(card);
    setSavedCardCvv('');
    setSavedCardCvvError('');
    setPaymentMethod('card');
    setPaymentForm({
      cardNumber: '',
      cardHolder: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: ''
    });
    setFormErrors({});
    setError(null);
  };

  const useDifferentCard = () => {
    setSelectedCard(null);
    setSavedCardCvv('');
    setSavedCardCvvError('');
    setPaymentMethod('checkout');
  };

  const handleSavedCardCvvChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setSavedCardCvv(value);
    
    if (value && !/^\d{3,4}$/.test(value)) {
      setSavedCardCvvError('CVV debe tener 3 o 4 dígitos');
    } else {
      setSavedCardCvvError('');
    }
  };

  const validateSavedCard = () => {
    if (!selectedCard) return false;
    
    if (!savedCardCvv || !/^\d{3,4}$/.test(savedCardCvv)) {
      setSavedCardCvvError('CVV requerido para tarjeta guardada');
      return false;
    }
    
    setSavedCardCvvError('');
    return true;
  };

  const processPayment = async () => {
    try {
      setProcessing(true);
      setError(null);
      setPaymentStep('processing');
      
      console.log('MercadoPagoModal: Iniciando proceso de pago:', {
        planId: planInfo.dbId,
        ciclo: billingCycle,
        precioOriginal,
        precioFinal,
        cuponId: cuponAplicado?.cupon?.id,
        saldoAplicado,
        paymentMethod
      });
      
      // Validar datos requeridos
      if (!planInfo || !planInfo.dbId) {
        throw new Error('Información del plan inválida');
      }
      
      if (!['mensual', 'anual'].includes(billingCycle)) {
        throw new Error('Ciclo de facturación inválido');
      }
      
      // Preparar payload base
      const paymentPayload = {
        planId: planInfo.dbId,
        cicloFacturacion: billingCycle,
        originalAmount: parseFloat(precioOriginal) || 0,
        finalAmount: parseFloat(precioFinal) || 0,
        cuponId: cuponAplicado?.cupon?.id || null,
        saldoAplicado: parseFloat(saldoAplicado) || 0
      };
      
      // Agregar datos de tarjeta si se requiere pago y se usa tarjeta guardada
      if (precioFinal > 0.01 && selectedCard) {
        paymentPayload.savedCardId = selectedCard.id;
        paymentPayload.cvv = savedCardCvv;
      }
      
      console.log('MercadoPagoModal: Payload para Mercado Pago:', {
        ...paymentPayload,
        cvv: paymentPayload.cvv ? '***' : undefined
      });
      
      // Caso 1: Precio Final es Cero (cupón o saldo cubre todo)
      if (precioFinal <= 0) {
        console.log('MercadoPagoModal: Precio 0. Activando plan sin pago...');
        
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setPaymentStep('complete');
        
        onPaymentSuccess({
          success: true,
          transactionId: `free-${Date.now()}`,
          message: cuponAplicado?.cupon.tipo === 'meses_gratis' 
            ? 'Plan gratuito aplicado con cupón' 
            : 'Compra cubierta con saldo',
          isFree: true,
          cuponAplicado: cuponAplicado,
          saldoAplicado: parseFloat(saldoAplicado) || 0,
          precioOriginal: parseFloat(precioOriginal) || 0,
          precioFinal: 0,
          planId: planInfo.dbId,
          cicloFacturacion: billingCycle,
          metodoPago: 'gratuito'
        });
        return;
      }
      
      // Caso 2: Precio Final > 0 (Pago real con Mercado Pago)
      console.log('MercadoPagoModal: Iniciando Mercado Pago');
      const response = await apiClient.post('/pagos/initialize', paymentPayload);
      
      console.log('MercadoPagoModal: Respuesta del backend:', response.data);
      
      if (!response.data || response.data.success === false) {
        throw new Error(response.data?.details || response.data?.error || 'Error al procesar el pago');
      }
      
      // Si es un plan gratuito (cubierto por cupón/saldo desde backend)
      if (response.data.isFree) {
        setPaymentStep('complete');
        onPaymentSuccess({
          success: true,
          transactionId: response.data.transactionId,
          message: response.data.message || 'Plan activado sin costo',
          isFree: true,
          cuponAplicado,
          saldoAplicado,
          precioOriginal,
          precioFinal: 0
        });
        return;
      }
      
      // Si el pago fue aprobado inmediatamente (tarjeta guardada)
      if (response.data.approved || response.data.status === 'approved') {
        setPaymentStep('complete');
        onPaymentSuccess({
          success: true,
          transactionId: response.data.transactionId,
          mpPaymentId: response.data.mpPaymentId,
          message: 'Pago aprobado exitosamente',
          cuponAplicado,
          saldoAplicado,
          precioOriginal,
          precioFinal,
          metodoPago: 'mercadopago',
          status: response.data.status
        });
        return;
      }
      
      // Si el pago está pendiente
      if (response.data.status === 'pending') {
        setPaymentStep('complete');
        onPaymentSuccess({
          success: true,
          transactionId: response.data.transactionId,
          mpPaymentId: response.data.mpPaymentId,
          message: 'Pago en proceso de verificación',
          cuponAplicado,
          saldoAplicado,
          precioOriginal,
          precioFinal,
          metodoPago: 'mercadopago',
          status: response.data.status,
          pending: true
        });
        return;
      }
      
      // Si hay preferencia de checkout, abrir en nueva ventana
      if (response.data.preferenceId && response.data.initPoint) {
        console.log('MercadoPagoModal: Abriendo checkout de Mercado Pago');
        
        setMpPreferenceId(response.data.preferenceId);
        setMpInitPoint(response.data.initPoint);
        setPaymentStep('checkout');
        
        // Guardar datos para verificar después
        localStorage.setItem('pendingMPTransaction', JSON.stringify({
          transactionId: response.data.transactionId,
          preferenceId: response.data.preferenceId,
          planId: planInfo.dbId,
          ciclo: billingCycle
        }));
        
        return;
      }
      
      throw new Error(response.data.message || 'Respuesta inesperada del servicio de pagos');
      
    } catch (err) {
      console.error('MercadoPagoModal: Error en processPayment:', err);
      setPaymentStep('error');
      
      let errorMessage = 'Ocurrió un error al procesar el pago';
      
      if (err.response?.data?.details) {
        errorMessage = err.response.data.details;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      if (onPaymentError) onPaymentError(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (processing) return;
    
    // Si no requiere pago (precio final = 0), procesar directamente
    if (precioFinal <= 0.01) {
      processPayment();
      return;
    }
    
    // Si se seleccionó una tarjeta guardada, validar CVV
    if (selectedCard) {
      if (validateSavedCard()) {
        processPayment();
      } else {
        setError("Por favor ingresa el CVV de tu tarjeta guardada.");
      }
      return;
    }
    
    // Para checkout de Mercado Pago, procesar directamente
    processPayment();
  };

  // Funciones para manejo de cupones (igual que PayU)
  const handleChangeCupon = (e) => {
    const code = e.target.value.toUpperCase().trim();
    setCodigo(code);
    setErrorCupon(null);
    
    if (cuponAplicado && code !== cuponAplicado.cupon.codigo) {
      setCuponAplicado(null);
    }
  };
  
  const aplicarCupon = async () => {
    if (!codigo.trim() || !planInfo) {
      setErrorCupon('Ingresa un código de cupón válido');
      return;
    }
    
    try {
      setLoadingCupon(true);
      setErrorCupon(null);
      setCuponAplicado(null);
      
      const payload = {
        codigo: codigo.trim(),
        planId: planInfo.dbId,
        ciclo: billingCycle,
        monto: precioOriginal
      };
      
      console.log('MercadoPagoModal: Validando cupón:', payload);
      
      const response = await apiClient.post('/cupones/validar', payload);
      
      console.log('MercadoPagoModal: Respuesta validación cupón:', response.data);
      
      if (response.data.valido) {
        setCuponAplicado(response.data);
        setError(null);
      } else {
        throw new Error(response.data.mensaje || 'Cupón inválido');
      }
    } catch (error) {
      console.error('MercadoPagoModal: Error al aplicar cupón:', error);
      const msg = error.response?.data?.details || error.response?.data?.error || error.message || 'Cupón inválido.';
      setErrorCupon(msg);
      setCuponAplicado(null);
    } finally {
      setLoadingCupon(false);
    }
  };
  
  const quitarCupon = () => {
    setCuponAplicado(null);
    setCodigo('');
    setErrorCupon(null);
  };
  
  // Manejar uso de saldo
  const handleUsarSaldo = () => {
    const desea = !usarSaldo;
    setUsarSaldo(desea);
    
    if (desea && saldo > 0) {
      const base = cuponAplicado ? 
        parseFloat(cuponAplicado.descuento.montoFinal || 0) : 
        precioOriginal;
      
      const aplicar = Math.min(saldo, base);
      setSaldoAplicado(aplicar);
    } else {
      setSaldoAplicado(0);
    }
  };

  const openMercadoPagoCheckout = () => {
    if (mpInitPoint) {
      // Abrir en nueva ventana
      const newWindow = window.open(mpInitPoint, '_blank', 'width=800,height=600');
      
      // Opcional: monitorear cierre de ventana
      const checkClosed = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(checkClosed);
          // Aquí podrías verificar el estado del pago
          console.log('Ventana de Mercado Pago cerrada');
        }
      }, 1000);
    }
  };

  if (!isOpen) return null;

  // VERSION MOVIL
  if (isMobile) {
    return (
      <AnimatePresence>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center">
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: "100%" },
              visible: { 
                opacity: 1, 
                y: 0,
                transition: { duration: 0.3, ease: "easeOut" }
              },
              exit: {
                opacity: 0,
                y: "100%",
                transition: { duration: 0.2 }
              }
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="bg-white rounded-t-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header móvil */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Plan {planInfo?.nombre || planInfo?.name}</h2>
                <p className="text-sm text-gray-600">{billingCycle === 'mensual' ? 'Mensual' : 'Anual'}</p>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                disabled={processing}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Contenido móvil */}
            <div className="flex-1 overflow-y-auto">
              {paymentStep === 'card-details' && (
                <div className="p-4 space-y-6">
                  {/* Resumen de precio móvil */}
                  <div className="bg-gradient-to-r from-blue-500 to-green-600 rounded-2xl p-4 text-white">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-blue-100">Precio base:</span>
                      <span>S/ {precioOriginal.toFixed(2)}</span>
                    </div>
                    
                    {cuponAplicado && cuponAplicado.descuento && cuponAplicado.descuento.montoDescuento > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-blue-100">Descuento cupón:</span>
                        <span className="text-green-300">-S/ {cuponAplicado.descuento.montoDescuento.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {saldoAplicado > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-blue-100">Saldo aplicado:</span>
                        <span className="text-green-300">-S/ {saldoAplicado.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="border-t border-blue-300 pt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Total a pagar:</span>
                        <span className="text-2xl font-bold">S/ {precioFinal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Saldo móvil */}
                  {saldo > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="flex items-center font-semibold text-gray-900 mb-3">
                        <Wallet className="w-5 h-5 mr-2 text-green-600" />
                        Tu saldo disponible
                      </h3>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xl font-bold text-gray-900">S/ {saldo.toFixed(2)}</p>
                          <p className="text-sm text-gray-600">Saldo disponible</p>
                        </div>
                        
                        {precioFinal > 0 && (
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={usarSaldo}
                              onChange={handleUsarSaldo}
                              className="sr-only"
                            />
                            <div className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                              usarSaldo ? 'bg-green-600' : 'bg-gray-300'
                            }`}>
                              <span className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                                usarSaldo ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            </div>
                            <span className="ml-2 text-sm font-medium text-gray-700">Usar</span>
                          </label>
                        )}
                      </div>
                      
                      {usarSaldo && saldoAplicado > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                          <p className="text-green-700 text-sm">
                            Se aplicarán S/ {saldoAplicado.toFixed(2)} de tu saldo
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cupón móvil */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="flex items-center font-semibold text-gray-900 mb-3">
                      <Tag className="w-5 h-5 mr-2 text-blue-600" />
                      ¿Tienes un cupón?
                    </h3>
                    
                    {!cuponAplicado ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={codigo}
                            onChange={handleChangeCupon}
                            placeholder="Código del cupón"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={loadingCupon}
                          />
                          <button
                            type="button"
                            onClick={aplicarCupon}
                            disabled={loadingCupon || !codigo.trim()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            {loadingCupon ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              'Aplicar'
                            )}
                          </button>
                        </div>
                        
                        {errorCupon && (
                          <div className="flex items-start text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                            <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{errorCupon}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-green-800">
                            <Check className="h-4 w-4 mr-2 text-green-600" />
                            <div>
                              <p className="font-medium text-sm">{cuponAplicado.cupon.descripcion}</p>
                              <p className="text-xs text-green-600">
                                Descuento: S/ {cuponAplicado.descuento.montoDescuento.toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <button 
                            type="button"
                            onClick={quitarCupon}
                            className="text-gray-500 hover:text-red-600 p-1 rounded-full"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Métodos de pago móvil */}
                  {precioFinal > 0.01 ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <h4 className="font-semibold text-gray-900">Método de pago</h4>
                      
                      {/* Tarjetas guardadas móvil */}
                      {savedCards.length > 0 && (
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-800">Tarjetas guardadas</h5>
                          <div className="space-y-2">
                            {savedCards.map(card => (
                              <div 
                                key={card.id}
                                onClick={() => selectSavedCard(card)}
                                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  selectedCard?.id === card.id 
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <CreditCard className="w-6 h-6 text-gray-400 mr-2" />
                                    <div>
                                      <p className="font-medium text-sm text-gray-900">•••• {card.lastFour}</p>
                                      <p className="text-xs text-gray-500">
                                        {card.brand?.toUpperCase()} • Exp: {card.expiryMonth}/{card.expiryYear}
                                      </p>
                                    </div>
                                  </div>
                                  {selectedCard?.id === card.id && (
                                    <Check className="w-5 h-5 text-blue-600" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* CVV para tarjeta guardada móvil */}
                          {selectedCard && (
                            <motion.div 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                            >
                              <div className="space-y-3">
                                <div className="flex items-center">
                                  <Shield className="w-4 h-4 text-yellow-600 mr-2" />
                                  <h6 className="font-medium text-yellow-900 text-sm">
                                    Verificación de seguridad
                                  </h6>
                                </div>
                                
                                <p className="text-sm text-yellow-700">
                                  Ingresa el CVV de tu tarjeta •••• {selectedCard.lastFour}
                                </p>
                                
                                <div>
                                  <label className="block text-sm font-medium text-yellow-900 mb-1">
                                    Código CVV
                                  </label>
                                  <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-yellow-600" />
                                    <input
                                      type="password"
                                      value={savedCardCvv}
                                      onChange={handleSavedCardCvvChange}
                                      placeholder="123"
                                      maxLength="4"
                                      className={`w-full pl-10 pr-3 py-2 border rounded-lg text-center focus:outline-none focus:ring-2 transition-colors ${
                                        savedCardCvvError 
                                          ? 'border-red-500 focus:ring-red-500 bg-red-50' 
                                          : 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500 bg-white'
                                      }`}
                                    />
                                  </div>
                                  {savedCardCvvError && (
                                    <p className="mt-1 text-sm text-red-600">{savedCardCvvError}</p>
                                  )}
                                </div>
                                
                                <button 
                                  type="button"
                                  onClick={useDifferentCard}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  Usar checkout de Mercado Pago
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </div>
                      )}

                      {/* Checkout de Mercado Pago como opción principal móvil */}
                      {!selectedCard && (
                        <div className="space-y-4">
                          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                            <div className="flex items-center mb-3">
                              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <Shield className="w-6 h-6 text-blue-600" />
                              </div>
                              <div>
                                <h5 className="font-semibold text-blue-900">Checkout Seguro</h5>
                                <p className="text-sm text-blue-700">Mercado Pago</p>
                              </div>
                            </div>
                            <p className="text-sm text-blue-800 mb-3">
                              Paga de forma segura con todas las opciones disponibles: tarjetas, Yape, PagoEfectivo y más.
                            </p>
                            <div className="flex items-center text-xs text-blue-700">
                              <Check className="w-4 h-4 mr-1" />
                              <span>Protección de compra garantizada</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Botón pagar móvil */}
                      <button
                        type="submit"
                        disabled={processing}
                        className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 disabled:opacity-50"
                      >
                        {processing ? (
                          <span className="flex items-center justify-center">
                            <Loader className="w-5 h-5 mr-2 animate-spin" />
                            Procesando...
                          </span>
                        ) : selectedCard ? (
                          `Pagar S/ ${precioFinal.toFixed(2)}`
                        ) : (
                          `Continuar con Mercado Pago - S/ ${precioFinal.toFixed(2)}`
                        )}
                      </button>
                      
                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <div className="flex items-start">
                            <AlertCircle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      )}
                    </form>
                  ) : (
                    // Plan gratuito móvil
                    <div className="text-center">
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-center mb-3">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <Check className="w-6 h-6 text-green-600" />
                          </div>
                        </div>
                        <h3 className="text-lg font-bold text-green-900 mb-1">¡Tu plan está listo!</h3>
                        <p className="text-sm text-green-700">
                          {cuponAplicado?.cupon.tipo === 'meses_gratis' 
                            ? `${cuponAplicado.cupon.valor} meses gratis` 
                            : 'Cubierto con tu saldo'}
                        </p>
                      </div>
                      
                      <button
                        onClick={processPayment}
                        disabled={processing}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 disabled:opacity-50"
                      >
                        {processing ? (
                          <span className="flex items-center justify-center">
                            <Loader className="w-5 h-5 mr-2 animate-spin" />
                            Activando...
                          </span>
                        ) : (
                          'Confirmar y activar plan'
                        )}
                      </button>
                      
                      {error && (
                        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                          <div className="flex items-start">
                            <AlertCircle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Estados móviles: Processing, Checkout, Complete, Error */}
              {paymentStep === 'processing' && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {precioFinal <= 0.01 ? 'Activando tu plan' : 'Procesando tu pago'}
                  </h3>
                  <p className="text-gray-600 text-center text-sm">
                    {precioFinal <= 0.01 
                      ? 'Configurando tu plan...'
                      : 'Procesando pago seguro con Mercado Pago...'}
                  </p>
                </div>
              )}

              {paymentStep === 'checkout' && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <ExternalLink className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Checkout de Mercado Pago</h3>
                  <p className="text-gray-600 text-center text-sm mb-6">
                    Serás redirigido al checkout seguro de Mercado Pago para completar tu pago.
                  </p>
                  <button
                    onClick={openMercadoPagoCheckout}
                    className="bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold py-3 px-6 rounded-xl mb-3"
                  >
                    Abrir Mercado Pago
                  </button>
                  <button
                    onClick={() => setPaymentStep('card-details')}
                    className="text-gray-600 text-sm"
                  >
                    Volver atrás
                  </button>
                </div>
              )}
              
              {paymentStep === 'complete' && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">¡Éxito!</h3>
                  <p className="text-gray-600 text-center text-sm mb-6">
                    Tu plan ha sido {precioFinal <= 0.01 ? 'activado' : 'pagado'} correctamente
                  </p>
                  <button
                    onClick={onClose}
                    className="bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold py-3 px-6 rounded-xl"
                  >
                    Continuar
                  </button>
                </div>
              )}
              
              {paymentStep === 'error' && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Error</h3>
                  <p className="text-red-600 text-center text-sm mb-1">{error}</p>
                  <p className="text-gray-600 text-center text-sm mb-6">Intenta nuevamente</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setPaymentStep('card-details');
                        setError(null);
                        setProcessing(false);
                      }}
                      className="bg-blue-600 text-white font-semibold py-3 px-4 rounded-xl"
                    >
                      Reintentar
                    </button>
                    <button
                      onClick={onClose}
                      className="bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-xl"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  // VERSION ESCRITORIO
  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div 
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex overflow-hidden"
          style={{ maxHeight: "90vh" }}
        >
          {/* Panel izquierdo - Información del plan CON SALDO Y CUPÓN */}
          <div className="w-2/5 bg-gradient-to-br from-blue-600 via-green-600 to-teal-700 text-white relative overflow-hidden">
            {/* Efectos de fondo */}
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
            
            <div className="relative h-full flex flex-col p-6 overflow-y-auto">
              <div className="flex-1 space-y-6">
                {/* Header del plan */}
                <div>
                  <h2 className="text-2xl font-bold mb-2">Plan {planInfo?.nombre || planInfo?.name}</h2>
                  <p className="text-blue-100 text-sm">
                    {billingCycle === 'mensual' ? 'Facturación mensual' : 'Facturación anual'}
                  </p>
                </div>

                {/* SALDO ARRIBA DEL RESUMEN */}
                {saldo > 0 && (
                  <motion.div 
                    variants={slideIn}
                    initial="hidden"
                    animate="visible"
                    className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                  >
                    <h3 className="flex items-center font-semibold mb-3">
                      <Wallet className="w-5 h-5 mr-2 text-green-300" />
                      Tu saldo disponible
                    </h3>
                    
                    {loadingSaldo ? (
                      <div className="flex items-center py-2">
                        <Loader className="h-5 w-5 mr-2 animate-spin" />
                        <span className="text-sm">Cargando saldo...</span>
                      </div>
                    ) : errorSaldo ? (
                      <div className="text-red-300 text-sm">{errorSaldo}</div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-2xl font-bold">S/ {saldo.toFixed(2)}</p>
                            <p className="text-blue-100 text-sm">Disponible para usar</p>
                          </div>
                          
                          {precioFinal > 0 && (
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={usarSaldo}
                                onChange={handleUsarSaldo}
                                className="sr-only"
                              />
                              <div className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                                usarSaldo ? 'bg-green-500' : 'bg-white/30'
                              }`}>
                                <span className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                                  usarSaldo ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                              </div>
                              <span className="ml-2 text-sm font-medium">Usar</span>
                            </label>
                          )}
                        </div>
                        
                        {usarSaldo && saldoAplicado > 0 && (
                          <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-3">
                            <p className="text-green-100 text-sm">
                              Se aplicarán S/ {saldoAplicado.toFixed(2)} de tu saldo
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
                
                {/* RESUMEN DE COMPRA */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <h3 className="text-lg font-semibold mb-4">Resumen de compra</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-blue-100">Precio base:</span>
                      <span className="font-medium">S/ {precioOriginal.toFixed(2)}</span>
                    </div>
                    
                    {cuponAplicado && cuponAplicado.descuento && cuponAplicado.descuento.montoDescuento > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-blue-100">Descuento cupón:</span>
                        <span className="font-medium text-green-300">-S/ {cuponAplicado.descuento.montoDescuento.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {saldoAplicado > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-blue-100">Saldo aplicado:</span>
                        <span className="font-medium text-green-300">-S/ {saldoAplicado.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="border-t border-white/20 pt-3 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-lg">Total a pagar:</span>
                        <span className="text-2xl font-bold">S/ {precioFinal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CUPÓN DEBAJO DEL RESUMEN */}
                <motion.div 
                  variants={slideIn}
                  initial="hidden"
                  animate="visible"
                  className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                >
                  <h3 className="flex items-center font-semibold mb-4">
                    <Tag className="w-5 h-5 mr-2 text-purple-300" />
                    ¿Tienes un cupón?
                  </h3>
                  
                  {!cuponAplicado ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={codigo}
                          onChange={handleChangeCupon}
                          placeholder="Ingresa tu código"
                          className="flex-1 px-3 py-2 bg-white/20 border border-white/30 rounded-lg placeholder-white/60 text-white focus:ring-2 focus:ring-white/50 focus:border-transparent"
                          disabled={loadingCupon}
                        />
                        <button
                          type="button"
                          onClick={aplicarCupon}
                          disabled={loadingCupon || !codigo.trim()}
                          className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 border border-white/30"
                        >
                          {loadingCupon ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            'Aplicar'
                          )}
                        </button>
                      </div>
                      
                      {errorCupon && (
                        <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-3">
                          <div className="flex items-start text-red-200 text-sm">
                            <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{errorCupon}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-green-100">
                          <Check className="h-5 w-5 mr-2 text-green-300" />
                          <div>
                            <p className="font-medium">{cuponAplicado.cupon.descripcion}</p>
                            <p className="text-sm text-green-200">
                              Descuento: S/ {cuponAplicado.descuento.montoDescuento.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <button 
                          type="button"
                          onClick={quitarCupon}
                          className="text-white/60 hover:text-red-300 p-1 rounded-full transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>

                {cuponAplicado && (
                  <motion.div 
                    variants={slideIn}
                    initial="hidden"
                    animate="visible"
                    className="bg-green-500/20 backdrop-blur-sm rounded-lg p-4 border border-green-400/30"
                  >
                    <div className="flex items-center">
                      <Gift className="w-5 h-5 mr-2 text-green-300" />
                      <div>
                        <p className="font-semibold text-green-100">¡Cupón aplicado!</p>
                        <p className="text-green-200 text-sm">{cuponAplicado.cupon.descripcion}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
              
              <div className="mt-auto pt-4">
                <div className="flex items-center text-xs text-blue-100">
                  <Shield className="h-4 w-4 mr-2" />
                  <span>Pago 100% seguro con Mercado Pago</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Panel derecho - Formulario de pago */}
          <div className="w-3/5 overflow-y-auto" style={{ maxHeight: "90vh" }}>
            {/* Botón cerrar */}
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10 p-2 rounded-full hover:bg-gray-100"
              disabled={processing}
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Contenido del panel derecho */}
            <div className="p-8">
              {/* Paso: Detalles de tarjeta */}
              {paymentStep === 'card-details' && (
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Datos de pago</h2>
                    <p className="text-gray-600">
                      Complete la información para procesar su compra
                    </p>
                  </div>
                  
                  {/* Formulario de tarjeta solo si se requiere pago */}
                  {precioFinal > 0.01 ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Tarjetas guardadas escritorio */}
                      {savedCards.length > 0 && (
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">Tarjetas guardadas</h4>
                          <div className="grid gap-3">
                            {savedCards.map(card => (
                              <div 
                                key={card.id}
                                onClick={() => selectSavedCard(card)}
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                                  selectedCard?.id === card.id 
                                    ? 'border-blue-500 bg-blue-50 shadow-md' 
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <CreditCard className="w-8 h-8 text-gray-400 mr-3" />
                                    <div>
                                      <p className="font-medium text-gray-900">•••• {card.lastFour}</p>
                                      <p className="text-sm text-gray-500">
                                        {card.brand?.toUpperCase()} • Exp: {card.expiryMonth}/{card.expiryYear}
                                      </p>
                                    </div>
                                  </div>
                                  {selectedCard?.id === card.id && (
                                    <Check className="w-6 h-6 text-blue-600" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Campo CVV para tarjeta guardada escritorio */}
                          {selectedCard && (
                            <motion.div 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
                            >
                              <div className="flex items-start space-x-3">
                                <Shield className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <h5 className="font-medium text-yellow-900 mb-2">
                                    Verificación de seguridad
                                  </h5>
                                  <p className="text-sm text-yellow-700 mb-3">
                                    Por tu seguridad, necesitamos el CVV de tu tarjeta •••• {selectedCard.lastFour}
                                  </p>
                                  
                                  <div className="max-w-xs">
                                    <label className="block text-sm font-medium text-yellow-900 mb-1">
                                      Código CVV
                                    </label>
                                    <div className="relative">
                                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-yellow-600" />
                                      <input
                                        type="password"
                                        value={savedCardCvv}
                                        onChange={handleSavedCardCvvChange}
                                        placeholder="123"
                                        maxLength="4"
                                        className={`w-full pl-10 pr-3 py-2 border rounded-lg text-center focus:outline-none focus:ring-2 transition-colors ${
                                          savedCardCvvError 
                                            ? 'border-red-500 focus:ring-red-500 bg-red-50' 
                                            : 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500 bg-white'
                                        }`}
                                      />
                                    </div>
                                    {savedCardCvvError && (
                                      <p className="mt-1 text-sm text-red-600">{savedCardCvvError}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                          
                          {selectedCard && (
                            <button 
                              type="button"
                              onClick={useDifferentCard}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                            >
                              Usar checkout de Mercado Pago
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* Checkout de Mercado Pago como opción principal escritorio */}
                      {!selectedCard && (
                        <div className="space-y-6">
                          <h4 className="font-semibold text-gray-900">Método de pago</h4>
                          
                          <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-xl p-6">
                            <div className="flex items-start space-x-4">
                              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Shield className="w-8 h-8 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <h5 className="text-lg font-semibold text-blue-900 mb-2">
                                  Checkout Seguro de Mercado Pago
                                </h5>
                                <p className="text-blue-800 mb-4">
                                  Paga de forma segura con todas las opciones disponibles: tarjetas de crédito y débito, 
                                  Yape, PagoEfectivo, transferencias bancarias y más.
                                </p>
                                <div className="grid grid-cols-2 gap-3 text-sm text-blue-700">
                                  <div className="flex items-center">
                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                    <span>Protección de compra</span>
                                  </div>
                                  <div className="flex items-center">
                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                    <span>Datos encriptados</span>
                                  </div>
                                  <div className="flex items-center">
                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                    <span>Múltiples métodos</span>
                                  </div>
                                  <div className="flex items-center">
                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                    <span>Soporte 24/7</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Mensaje de seguridad */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start">
                          <Shield className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Pago 100% seguro</p>
                            <p className="text-xs text-blue-700 mt-1">
                              Tu información es encriptada y protegida por Mercado Pago con los más altos estándares de seguridad internacionales.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Botón de pago */}
                      <button
                        type="submit"
                        disabled={processing}
                        className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        {processing ? (
                          <span className="flex items-center justify-center">
                            <Loader className="w-5 h-5 mr-2 animate-spin" />
                            Procesando pago...
                          </span>
                        ) : selectedCard ? (
                          `Pagar S/ ${precioFinal.toFixed(2)} con tarjeta guardada`
                        ) : (
                          `Continuar con Mercado Pago - S/ ${precioFinal.toFixed(2)}`
                        )}
                      </button>
                      
                      {error && !processing && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <div className="flex items-start">
                            <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      )}
                    </form>
                  ) : (
                    // Plan gratuito escritorio
                    <div className="text-center">
                      <div className="bg-green-50 border border-green-200 rounded-xl p-8 mb-8">
                        <div className="flex items-center justify-center mb-6">
                          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                            <Check className="w-10 h-10 text-green-600" />
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-green-900 mb-3">¡Tu plan está listo!</h3>
                        <p className="text-green-700 text-lg">
                          {cuponAplicado?.cupon.tipo === 'meses_gratis' 
                            ? `Disfruta de tus ${cuponAplicado.cupon.valor} meses gratis con este cupón.` 
                            : 'Tu compra está completamente cubierta con tu saldo disponible.'}
                        </p>
                      </div>
                      
                      <button
                        onClick={processPayment}
                        disabled={processing}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      >
                        {processing ? (
                          <span className="flex items-center justify-center">
                            <Loader className="w-5 h-5 mr-2 animate-spin" />
                            Activando plan...
                          </span>
                        ) : (
                          'Confirmar y activar plan'
                        )}
                      </button>
                      
                      {error && !processing && (
                        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
                          <div className="flex items-start">
                            <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              
              {/* Paso: Procesando */}
              {paymentStep === 'processing' && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                    <Loader className="w-10 h-10 text-blue-600 animate-spin" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {precioFinal <= 0.01 ? 'Activando tu plan' : 'Procesando tu pago'}
                  </h3>
                  <p className="text-gray-600 text-center max-w-md">
                    {precioFinal <= 0.01 
                      ? 'Estamos configurando tu plan. Esto solo tomará unos segundos.'
                      : 'Estamos procesando tu pago de forma segura con Mercado Pago. Por favor no cierres esta ventana.'}
                  </p>
                  
                  <div className="mt-8 flex items-center space-x-2 text-sm text-gray-500">
                    <Shield className="w-4 h-4" />
                    <span>Transacción segura con cifrado SSL</span>
                  </div>
                </div>
              )}

              {/* Paso: Checkout de Mercado Pago */}
              {paymentStep === 'checkout' && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                    <ExternalLink className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Checkout de Mercado Pago
                  </h3>
                  <p className="text-gray-600 text-center max-w-md mb-8">
                    Serás redirigido al checkout seguro de Mercado Pago donde podrás elegir tu método 
                    de pago preferido y completar la transacción de forma segura.
                  </p>
                  
                  <div className="space-y-4">
                    <button
                      onClick={openMercadoPagoCheckout}
                      className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Abrir Mercado Pago
                    </button>
                    
                    <button
                      onClick={() => setPaymentStep('card-details')}
                      className="block text-gray-600 hover:text-gray-800 text-sm transition-colors"
                    >
                      ← Volver atrás
                    </button>
                  </div>
                  
                  <div className="mt-8 bg-blue-50 rounded-lg p-4 max-w-md">
                    <p className="text-blue-800 text-sm">
                      <strong>Nota:</strong> Una vez completado el pago en Mercado Pago, 
                      regresa aquí para confirmar la activación de tu plan.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Paso: Completo */}
              {paymentStep === 'complete' && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {precioFinal <= 0.01 ? '¡Plan activado exitosamente!' : '¡Pago completado!'}
                  </h3>
                  <p className="text-gray-600 text-center max-w-md mb-8">
                    {precioFinal <= 0.01 
                      ? `Tu plan ${planInfo?.nombre || planInfo?.name} ha sido activado. Ya puedes disfrutar de todos sus beneficios.`
                      : `Tu pago ha sido procesado correctamente con Mercado Pago. Tu plan ${planInfo?.nombre || planInfo?.name} está ahora activo.`}
                  </p>
                  
                  <div className="bg-green-50 rounded-lg p-4 mb-6 max-w-md">
                    <div className="flex items-center text-green-800">
                      <Check className="w-5 h-5 mr-2 text-green-600" />
                      <p className="text-sm">
                        {precioFinal <= 0.01 
                          ? 'Plan activado sin costo adicional'
                          : 'Recibirás un email de confirmación en breve'}
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={onClose}
                    className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Continuar
                  </button>
                </div>
              )}
              
              {/* Paso: Error */}
              {paymentStep === 'error' && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
                    <AlertCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {precioFinal <= 0.01 ? 'Error en la activación' : 'Error en el pago'}
                  </h3>
                  <p className="text-red-600 text-center max-w-md mb-2 font-medium">
                    {error || (precioFinal <= 0.01 
                      ? 'Ocurrió un error al activar tu plan.' 
                      : 'Ocurrió un error al procesar tu pago con Mercado Pago.')}
                  </p>
                  <p className="text-gray-600 text-center max-w-md mb-8">
                    Por favor verifica tu información e intenta nuevamente. Si el problema persiste, 
                    contacta con nuestro soporte.
                  </p>
                  
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setPaymentStep('card-details');
                        setError(null);
                        setProcessing(false);
                      }}
                      className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Reintentar
                    </button>
                    <button
                      onClick={onClose}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-xl transition-colors duration-300"
                    >
                      Cancelar
                    </button>
                  </div>
                  
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      ¿Necesitas ayuda? Contacta con soporte: 
                      <a href="mailto:soporte@inkayros.com" className="text-blue-600 hover:text-blue-800 ml-1">
                        soporte@inkayros.com
                      </a>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MercadoPagoModal;